import { formatDate } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Directive, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { EmailSendDocumentRequest } from "@models/email-templates/email-bulk-send-request";
import { EmailMultipleAddress } from "@models/email-templates/email-multiple-address";
import { EmailPayloadBase } from "@models/email-templates/email-payload-base";
import { EmailTemplate } from "@models/email-templates/email-template";
import { NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import { AnnualStatementsService } from "@shared/services/annual-statements.service";
import { DocumentsApiService } from "@shared/services/documents-api.service";
import { DocumentsService } from "@shared/services/documents.service";
import { EmailsApiService } from "@shared/services/emails-api.service";
import { SignalRService } from "@shared/services/signalr.service";
import { bytesToMegabytes } from "@shared/utils/bytes-to-megabytes";
import { Guid } from "@shared/utils/guid.helper";
import { toBase64 } from "@shared/utils/to-base64";
import { DatepickerHelper } from "@shared/validators/date-picker-form-validators";
import { catchError, EMPTY, filter, finalize, from, Observable, of, switchMap, tap } from "rxjs";
import { AnnualStatement } from "@models/annual-statements/annual-statement";
import { Document } from "@models/documents/document";
import { DocumentSigning } from "@models/documents/document-signing";
import { Signature } from "@models/documents/signature";
import { SendOption, SignColumn, SigningStatus } from "@models/annual-statements/annual-statement-enums";
import { ApproverStatus } from "@models/documents/approver-status.enum";
import { EsignProvider } from "@models/integrations/esign-provider.enum";
import { IntegrationConnectionsEnum } from "@models/integrations/integration-connections.enum";
import { CarbonCopy, ESignBody } from "@models/documents/sign-documents";
import {
  CommonActionsModalComponent,
  IActionButtonConfig
} from "@shared/components/commons/common-actions-modal/common-actions-modal.component";
import { ConfirmModalComponent } from "@shared/components/commons/confirm/confirm-modal.component";
import {
  eSignMessage,
  getUploadFileNotification,
  paperSignMessage,
  voidEnvelopeMessage,
  voidPaperSignMessage
} from "./constants/sign-documents-text-messages";
import { SignDocumentsGridService } from "./sign-documents.grid.service";

@Directive()
export abstract class SignDocumentsService extends SignDocumentsGridService implements OnInit {
  protected emailTemplatesService = inject(EmailsApiService);
  protected signalRService = inject(SignalRService);
  protected annualStatementsService = inject(AnnualStatementsService);
  protected documentsService = inject(DocumentsService);
  protected documentsApiService = inject(DocumentsApiService);

  protected readonly EsignProvider = EsignProvider;
  protected readonly SigningStatus = SigningStatus;
  protected readonly IntegrationConnectionsEnum = IntegrationConnectionsEnum;

  protected readonly maxCarbonCopyNumber = 15;
  protected readonly eSignMsg = eSignMessage;
  protected readonly paperSignMsg = paperSignMessage;

  readonly allowedMaxFileSizeMB = 5;
  readonly allowedTotalFileSizeMB = 10;
  readonly maxFilesNumber = 10;
  readonly acceptedFormat = '.pdf';
  readonly uploadNotification = getUploadFileNotification(this.allowedMaxFileSizeMB, this.allowedTotalFileSizeMB, this.maxFilesNumber);

  errorMessage = '';
  signErrorMessage = '';
  uploadedFiles: File[] = [];
  signingDate = new Date();
  minDate!: NgbDateStruct;
  maxDate!: NgbDateStruct;

  formattedSigningDate = '';
  payloadModel!: EmailPayloadBase;
  SendOption = SendOption;
  SignColumn = SignColumn;
  emailList: EmailMultipleAddress[] = [];
  carbonCopyEmails: EmailMultipleAddress[] = [];

  template!: EmailTemplate;
  loading = false;
  envelopeLoading = false;
  disabledVoidEnvelopeBtn = false;
  includeAttachedFiles = true;
  selectedESignConnection: IntegrationConnectionsEnum | null = null;

  override ngOnInit(): void {
    super.ngOnInit();
    this.showVoidEnvelope(this.isEnvelopeSent);
    this.listenSignalRMessages();

    this.setSendByOption();
    this.setSigningDateParams();
    this.setApprover();
    this.setSignatories();
    this.setCarbonCopies();
    this.setGridRows();
    this.listenSendByButton();
    this.listenHeaderButton();

    if (this.isEnvelopeSent) {
      this.setEnvelopeGridConfig();
      this.showStatuses(true);
    } else {
      this.setGridConfig();
    }
  }

  ngOnDestroy(): void {
    this.showSendPaperBtn(false);
    this.showSendEmailBtn(false);
  }

  public setSendByOption(): void {
    if (this.documentSigning?.signedByPaper) {
      this.sendByOptions.filter(o => o.value === SendOption.Sign)[0].disabled = true;
      this.form.controls.sendBy.patchValue(SendOption.Paper);
      this.showSendPaperBtn(true);
      this.showSendEmailBtn(false);
    } else if (this.documentSigning?.signingStatus === SigningStatus.Signed) {
      this.sendByOptions.filter(o => o.value === SendOption.Paper)[0].disabled = true;
      this.form.controls.sendBy.patchValue(SendOption.Sign);
    } else if (Document.isDocumentInvoice(this.documentBase)) {
      this.sendByOptions.filter(o => o.value === SendOption.Sign)[0].disabled = true;
      this.sendByOptions.filter(o => o.value === SendOption.Paper)[0].disabled = true;
      this.sendByOptions = this.sendByOptions.filter(o => o.value === SendOption.Email);
      this.form.controls.sendBy.patchValue(SendOption.Email);
    } else {
      if (this.form.controls.sendBy.value === SendOption.Sign) {
        this.setSignatories();
      }

      this.showSendEmailBtn(this.form.controls.sendBy.value === SendOption.Email);
    }
  }

  public listenSendByButton(): void {
    this.form.controls.sendBy.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((sendBy: SendOption | null) => {
        if (sendBy === SendOption.Sign) {
          this.setSignatories();
        }

        this.showSendEmailBtn(sendBy === SendOption.Email);
        this.showSendPaperBtn(sendBy === SendOption.Paper);
      });
  }

  public setSigningDateParams(): void {
    const signedOn = this.documentSigning?.signedOn ? new Date(this.documentSigning.signedOn) : new Date();
    this.signingDate = signedOn;
    this.formattedSigningDate = formatDate(signedOn, 'yyyy-MM-dd', 'en-US');
    this.minDate = DatepickerHelper.getStructFromDate(signedOn);
  }

  refreshSigningInfo(): void {
    if (!this.documentSigning) {
      return;
    }

    this.documentSigningsService
      .getDocumentSigning(this.documentSigning.documentSigningId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (documentSigning) => {
          this.documentSigning = documentSigning;
          this.updateView();
        },
        error: (err) => {
          console.error('Error fetching document signing info:', err);
        },
      });
  }

  private updateView(): void {
    this.setSigningDateParams();
    this.setApprover();
    this.setSignatories();
    this.setCarbonCopies();
    this.setGridRows();
  }

  public listenSignalRMessages(): void {
    this.signalRService.onDocumentSigningStatusChanged()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (message) => {
          if (this.shouldRefreshSigningInfo(message)) {
            console.log('Refreshing signing info for message:', message);
            this.refreshSigningInfo();
          }
        },
        error: (err) => {
          console.error('Error handling SignalR message:', err);
        },
      });
  }

  private shouldRefreshSigningInfo(message: string): boolean {
    return message === this.documentBase.documentId
      && this.documentSigning?.signingStatus !== SigningStatus.ReadyToSend;
  }

  public listenHeaderButton(): void {
    this.confirmSentESignAction$
      ?.pipe(
        filter(confirm => confirm),
        switchMap(() => this.sendToESign()),
        tap(() => this.confirmSentESignAction$?.next(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.confirmPaperSignAction$
      ?.pipe(
        filter(confirm => confirm),
        switchMap(() => {
          return this.uploadedFiles.length
            ? this.paperSign(this.documentBase.documentId ?? '')
            : this.confirmPaperSign();
        }),
        tap(() => this.confirmPaperSignAction$?.next(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.confirmSendEmailAction$
      ?.pipe(
        filter(confirm => confirm),
        switchMap(() => this.sendEmail()),
        tap(() => this.confirmSendEmailAction$?.next(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  public showStatuses(show: boolean): void {
    this.showVoidEnvelope(this.isEnvelopeSent);
    this.colDefs[this.SignColumn.Status].hide = !show;
    this.colDefs = [...this.colDefs];
  }

  public sendToESign(): Observable<DocumentSigning> {
    if (!this.signatories.length || !this.template) {
      this.toastr.error('Signatories and email are required', 'Error');
      return EMPTY;
    }

    const signatoriesHasEmail = this.signatories.every(item => item.email);
    if (!signatoriesHasEmail) {
      this.toastr.error('Signatories email is required', 'Error');
      return EMPTY;
    }

    let signatures: Signature[] = [];
    if (this.documentSigning?.signatures?.length) {
      signatures = this.documentSigning.signatures.map(((item, index) => {
        item.email = this.signatories[index].email;
        item.phone = this.signatories[index].phone;
        item.signatory.fullName = this.signatories[index].fullName;
        item.signatory.firstName = this.signatories[index].firstName ?? '';
        item.signatory.lastName = this.signatories[index].lastName ?? '';

        return item;
      }));
    }

    const approver = {
      recipientId: '',
      email: this.approver?.email ?? '',
      phone: this.approver?.phone ?? '',
      fullName: this.approver?.fullName ?? '',
      emailSendId: Guid.EmptyGuid,
      status: ApproverStatus.WaitingForApproval
    };

    const carbonCopyList: CarbonCopy[] = this.carbonCopy?.map(item => ({
      recipientId: '',
      email: item.email,
      phone: item.phone,
      fullName: item.fullName,
      emailSendId: Guid.EmptyGuid,
    }));

    const eSignBody = {
      signatures,
      carbonCopyList,
      approver: this.approver?.email ? approver : null,
      carbonCopyEmails: this.carbonCopy.map(item => item.email),
      payloadModel: this.payloadModel,
      emailTemplate: this.template,
      documentsToAttached: this.annualStatementsService.documentsToAttach().filter(x => x.selected).map(x => x.documentId),
      includeAttachedFiles: this.includeAttachedFiles
    };

    this.sendSignLoading(true);
    this.disableRow(true);
    const documentId = this.documentBase.documentId ?? '';

    return this.eSignService.sendToESign(documentId, eSignBody)
      .pipe(
        tap(res => {
          if (res) {
            this.reloadDocument.emit(true);
            this.showStatuses(true);
            this.sendSignLoading(false);
            this.disableRow(false);

            if (eSignBody.approver) {
              this.toastr.success("Your documents were successfully sent to the approver for the review", "Success");
            } else {
              this.toastr.success("Your documents were successfully sent for eSigning", "Success");
            }
          }
        }),
        catchError((error: HttpErrorResponse) => {
          this.sendSignLoading(false);
          this.disableRow(false);
          this.setSignErrorMessage(error.error?.message as string);
          this.toastr.error('eSign error', 'Error');
          console.error(error);
          return EMPTY;
        })
      );
  }

  protected sendEmail(): Observable<string> {
    this.sendSignLoading(true);
    const documentsToAttachedId = this.annualStatementsService.documentsToAttach()
      .filter(x => x.selected)
      .map(x => x.documentId);

    const emailSendRequest: EmailSendDocumentRequest = {
      recipientsList: this.emailList.map(item => item.email),
      payloadModel: this.payloadModel,
      template: this.template,
      documentsToAttachedId: documentsToAttachedId,
      includeAttachedFiles: this.includeAttachedFiles
    };

    return this.emailTemplatesService.sendDocument(emailSendRequest, this.documentBase.documentId ?? '')
      .pipe(
        tap(() => {
          this.toastr.success('Email(s) has been successfully sent', 'Success');
          this.refreshSignEntity$.next();
          this.emailList = [];
          this.carbonCopyEmails = [];
        }),
        catchError(err => {
          this.toastr.error('Send email error', 'Error');
          console.error(err);

          return EMPTY;
        }),
        finalize(() => {
          this.sendSignLoading(false);
        })
      );
  }

  protected paperSign(documentId: string): Observable<AnnualStatement | DocumentSigning> {
    this.loading = true;
    this.sendSignLoading(true);
    this.disableHeaderBtn(true);

    return from(Promise.all(
      this.uploadedFiles.map(async (file: File) => ({
        fileName: file.name,
        base64FileContent: await toBase64(file),
      }))
    ))
      .pipe(
        switchMap(attachmentsToUpload => {
          const paperSignBody = {
            signedDate: this.formattedSigningDate,
            attachmentsToUpload
          };

          return this.eSignService.paperSign(documentId, paperSignBody)
            .pipe(
              tap(() => {
                this.toastr.success("Paper sign document confirmed", "Success");
                this.reloadDocument.emit(true);
              }),
              catchError(err => {
                this.toastr.error('Paper sign error', 'Error');
                console.error(err);
                return EMPTY;
              })
            );
        }),
        finalize(() => {
          this.loading = false;
          this.sendSignLoading(false);
          this.disableHeaderBtn(false);
        }),
      );
  }

  public voidEnvelope(): void {
    this.sendSignLoading(true);
    this.envelopeLoading = true;

    const documentId = this.documentBase.documentId ?? '';
    const modalRef = this.modalService.open(ConfirmModalComponent);
    const instance = modalRef.componentInstance as ConfirmModalComponent;

    instance.title = `Warning`;
    instance.message = voidEnvelopeMessage;
    instance.confirmText = 'Continue';
    instance.confirm = () => {
      return this.eSignService.voidEnvelope(documentId).pipe(
        tap(() => {
          this.setGridConfig();
          this.showStatuses(false);
          this.sendByOptions = this.sendByOptions.map(o => {
            o.disabled = false;
            return o;
          });
          this.reloadDocument.emit(true);
          this.toastr.success('Envelope was voided', 'Success');
        }),
        catchError(err => {
          this.toastr.error('There was an error processing the envelope', 'Error');
          console.error(err);
          return of('ERR');
        }),
      );
    };

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.sendSignLoading(false);
        this.envelopeLoading = false;
      });
  }

  public voidPaperSign(): void {
    this.envelopeLoading = true;
    const documentId = this.documentBase.documentId ?? '';

    const modalRef = this.modalService.open(ConfirmModalComponent);
    const instance = modalRef.componentInstance as ConfirmModalComponent;

    instance.title = `Warning`;
    instance.message = voidPaperSignMessage;
    instance.confirmText = 'Continue';
    instance.confirm = () => {
      return this.eSignService.voidPaperSign(documentId).pipe(
        tap(() => {
          this.setGridConfig();
          this.showStatuses(false);
          this.sendByOptions = this.sendByOptions.map(o => {
            o.disabled = false;
            return o;
          });
          this.reloadDocument.emit(true);
          this.toastr.success('Paper signing was voided', 'Success');
          this.showSendPaperBtn(false);
        }),
        catchError(err => {
          this.toastr.error('There was an error processing the envelope', 'Error');
          console.error(err);
          return of('ERR');
        }),
      );
    };

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.sendSignLoading(false);
        this.envelopeLoading = false;
      });
  }

  public onChangeEmail(emailData: Partial<ESignBody>): void {
    this.template = emailData.emailTemplate as unknown as EmailTemplate;
    this.payloadModel = emailData.payloadModel as unknown as EmailPayloadBase;
  }

  public setSignatories(): void {
    const signatures = this.documentSigning?.signatures ?? [];

    this.signatories = signatures.map((signature: Signature) => ({
      id: signature.id ?? "",
      fullName: signature.signatory.fullName,
      firstName: signature.signatory.firstName,
      lastName: signature.signatory.lastName,
      email: signature.email,
      phone: signature.phone,
      status: signature.status,
      signatureId: signature.signatoryId,
      signedOn: signature.signedOn,
      disabled: false
    }));

    this.disableHeaderBtn(
      !this.signatories.length
      || !this.signatories.every(item => item.email)
      || this.isEnvelopeSent && this.documentSigning?.signingStatus !== SigningStatus.Signed
    );
  }

  public setCarbonCopies(): void {
    if (this.documentSigning?.carbonCopies?.length) {
      this.carbonCopy = this.documentSigning.carbonCopies.map(item => ({
        id: '',
        fullName: item.fullName,
        email: item.email,
        phone: item.phone,
        status: this.documentSigning?.signingStatus ?? 0,
        signatureId: '',
        disabled: false,
      }));
    }
  }

  onChangeEmailList(emailList: string[]): void {
    this.disableHeaderBtn(!emailList.length);
  }

  onSigningDateChange(signingDate: Date | NgbDateStruct): void {
    if (signingDate && this.uploadedFiles.length) {
      const date = signingDate instanceof Date ? signingDate : DatepickerHelper.getDateFromStruct(signingDate);
      this.formattedSigningDate = formatDate(date, 'yyyy-MM-dd', 'en-US');
    }

    this.disableHeaderBtn(!signingDate);
  }

  onFilesLoaded(files: File[]): void {
    if (this.uploadedFiles.length + files.length > this.maxFilesNumber) {
      this.setErrorMessage(`The maximum number of files to upload is ${ this.maxFilesNumber }. Please use the allowed number of files.`);
      return;
    }

    const isAllowedFileExtension = files.some(file => {
      return file.name.endsWith(this.acceptedFormat);
    });
    if (!isAllowedFileExtension) {
      this.setErrorMessage(`Uploaded file has not allowed file extension. Please use one of allowed file extensions: ${ this.acceptedFormat }`);
      return;
    }

    const isNotAllowedFileSize = files.some(file => bytesToMegabytes(file.size) > this.allowedMaxFileSizeMB);
    if (isNotAllowedFileSize) {
      this.setErrorMessage(`It seems that you have uploaded a file that exceeds the allowed size. Please ensure that the file is not larger than ${ this.allowedMaxFileSizeMB } MB`);
      return;
    }

    const totalFilesSize = files.reduce((acc, file) => acc + file.size, 0);
    if (bytesToMegabytes(totalFilesSize) > this.allowedTotalFileSizeMB) {
      this.setErrorMessage(`Uploaded files has not allowed file is too big. Max file size is ${ this.allowedMaxFileSizeMB } MB`);
      return;
    }

    this.uploadedFiles = [...this.uploadedFiles, ...files];

    if (this.signingDate) {
      this.disableHeaderBtn(false);
    }

    this.setErrorMessage('');
  }

  checkSentNotification() {
    const notificationDetails = this.documentBase.notificationDetails;
    if (notificationDetails?.sentToEmails && notificationDetails.sentToEmails.length > 0) {
     return true;
    }

    return false;
  }

  setErrorMessage(errorMessage: string): void {
    this.errorMessage = errorMessage;
  }

  setSignErrorMessage(errorMessage: string): void {
    if (errorMessage?.includes('Attached document error.')) {
      this.signErrorMessage = 'Attached document error. Please check the attached documents. The signature in attached documents should be the same as the signature in the document.';
      return;
    }
  }

  deleteFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
  }

  confirmPaperSign(): Observable<unknown> {
    const modalRef = this.modalService.open(CommonActionsModalComponent, { size: 'sm' });
    const instance = modalRef.componentInstance as CommonActionsModalComponent;
    const instanceDestroyRef = instance.getDestroyRef();

    const actions: IActionButtonConfig[] = [
      {
        text: 'Yes',
        action: this.paperSign(this.documentBase.documentId ?? '')
          .pipe(takeUntilDestroyed(instanceDestroyRef))
      },
      {
        text: 'Cancel',
        btnColor: 'white',
        action: of(null)
          .pipe(
            takeUntilDestroyed(instanceDestroyRef),
            tap(() => instance.close())
          )
      },
    ];

    const body = `
        <div class="text-center">No documentation has been uploaded.</div>
        <div class="text-center">Would you like to proceed?</div>
    `;

    instance.config = {
      header: 'Confirm Signing',
      iconClass: '',
      showCloseBtn: false,
      body,
      actions
    };

    return from(modalRef.result.catch(() => null));
  }

  get isEnvelopeSent(): boolean  {
    return this.documentSigning?.signingStatus === SigningStatus.Sent
      || this.documentSigning?.signingStatus === SigningStatus.Created
      || this.documentSigning?.signingStatus === SigningStatus.Signed
      || this.documentSigning?.signingStatus === SigningStatus.Rejected;
  }
}
